import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { Router } from '@angular/router';
import { SanitizationService } from 'src/app/core/services/ui/sanitization.service';
import { Message } from 'src/app/core/interfaces/api/message.interfaces';
import { MessageService } from 'src/app/core/services/api/message.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'shared-header-message-box',
  templateUrl: './message-box.component.html',
  styleUrls: ['./message-box.component.scss'],
})
export class MessageBoxComponent implements OnInit, OnDestroy {
  @Input() mobileMode: boolean = false;
  @Input() messageCount?: number;
  @Output() messageCountChange = new EventEmitter<number>();

  public messages: Message[] = [];
  public noMoreMessages = false;
  public isLoading: boolean = false;
  public hasNextPage: boolean = false;
  public hasPrevPage: boolean = false;
  public showMessages: boolean = false;
  public limit: number = 10;
  public totalCount: number = 0;
  public totalPages: number = 1;
  public currentPage: number = 1;
  private routeAll: string = '/players/players-chat';

  private hoverTimeout: any;

  constructor(
    private _messageService: MessageService,
    private _router: Router,
    private _translate: TranslateService,
    private _sanitizationService: SanitizationService,
  ) {}

  ngOnInit() {
    if (this.messageCount !== undefined) {
      this.totalCount = this.messageCount;
    }

    if (!this.mobileMode) {
      this.getAllMessages();
    }
  }

  ngOnDestroy(): void {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    this.enableBodyScroll();
  }

  private getAllMessages(page: number = 1): void {
    if (this.noMoreMessages) return;
    if (!this.canLoadMoreMessages(page)) return;

    this.isLoading = true;
    const filter = { page, limit: this.limit };

    this._messageService.getAllUnreadMessages(filter).subscribe({
      next: (response: any) => this.handleMessagesSuccess(response, page),
      error: () => this.handleMessagesError(),
    });
  }

  private canLoadMoreMessages(page: number): boolean {
    return !this.isLoading && (page === 1 || this.hasNextPage);
  }

  private handleMessagesSuccess(response: any, page: number): void {
    const data = response.data.result || response;

    const sanitizedMessages = (data?.result || []).map((message: Message) => ({
      ...message,
      subject: this._sanitizationService.sanitizeText(message.subject),
      message: this._sanitizationService.sanitizeText((message as any).message),
    }));

    this.updateMessagesList(sanitizedMessages, page);
    this.updatePaginationState(data);
    this.isLoading = false;

    if (
      (sanitizedMessages.length === 0 || this.totalCount === 0) &&
      page === 1
    ) {
      this.noMoreMessages = true;
    }

    if (page === 1) {
      this.emitMessageCount();
    }
  }

  private updateMessagesList(newMessages: Message[], page: number): void {
    this.messages =
      page === 1 ? newMessages : [...this.messages, ...newMessages];
  }

  private updatePaginationState(data: any): void {
    this.totalCount = data?.totalCount || 0;
    this.totalPages = data?.totalPages || 1;
    this.currentPage = data?.page || 1;
    this.hasNextPage = data?.hasNextPage || false;
    this.hasPrevPage = data?.hasPrevPage || false;
  }

  private emitMessageCount(): void {
    this.messageCountChange.emit(this.totalCount);
  }

  private handleMessagesError(): void {
    this.isLoading = false;
  }

  public getTimeAgo(date: Date | string | undefined): string {
    if (!date) return '';
    const now = new Date();
    const messageDate = new Date(date);
    const diffTime = Math.abs(now.getTime() - messageDate.getTime());
    const diffMinutes = Math.ceil(diffTime / (1000 * 60));

    if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    } else if (diffMinutes < 1440) {
      const hours = Math.floor(diffMinutes / 60);
      return `${hours}h`;
    } else {
      const days = Math.floor(diffMinutes / 1440);
      return `${days}d`;
    }
  }

  public getSenderName(message: any): string {
    return this.getUserName(message.fromId);
  }

  public getReceiverName(message: any): string {
    return this.getUserName(message.toId);
  }

  private getUserName(user: any): string {
    if (!user) return this.getUnknownUserLabel();

    const fullName = this.buildFullName(user);
    const username = this.extractUsername(user);

    return fullName || username || this.getUnknownUserLabel();
  }

  private buildFullName(user: any): string {
    const firstName = this.extractFirstName(user);
    const lastName = this.extractLastName(user);
    const username = this.extractUsername(user);
    return `${firstName} ${lastName} - ${username}`.trim();
  }

  private extractFirstName(user: any): string {
    return user.personalInfo?.firstName || user.firstName || '';
  }

  private extractLastName(user: any): string {
    return user.personalInfo?.lastName || user.lastName || '';
  }

  private extractUsername(user: any): string {
    return user.personalInfo?.username || user.username || '';
  }

  private getUnknownUserLabel(): string {
    return this._translate.instant('profile.messageBox.unknownUser');
  }

  public isAdminView(): boolean {
    return false;
  }

  public getParticipantLabel(user: any, userType: string | undefined): string {
    if (!user || !userType)
      return this._translate.instant('players.chats.conversation.unknown');

    let firstName = '';
    let lastName = '';

    if (user.personalInfo) {
      firstName = user.personalInfo.firstName || '';
      lastName = user.personalInfo.lastName || '';
    } else {
      firstName = user.firstName || '';
      lastName = user.lastName || '';
    }

    const name =
      `${firstName} ${lastName}`.trim() ||
      user.username ||
      this._translate.instant('profile.messageBox.unknownUser');

    const type =
      userType.toLowerCase() === 'player'
        ? this._translate.instant('players.chats.conversation.player')
        : this._translate.instant('players.chats.conversation.agent');
    return `${name} (${type})`;
  }

  public trackByMessageId(index: number, message: any): string {
    return message._id;
  }

  public toggleMessages(): void {
    this.showMessages = !this.showMessages;

    if (this.showMessages) {
      if (
        (this.mobileMode || this.messages.length === 0) &&
        !this.noMoreMessages
      ) {
        this.getAllMessages();
      }
      this.disableBodyScroll();
    } else {
      this.enableBodyScroll();
      this.noMoreMessages = false;
      this.messages = [];
      this.currentPage = 1;
      this.hasNextPage = false;
      this.hasPrevPage = false;
    }

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
  }

  public closeMessages(): void {
    this.showMessages = false;
    this.enableBodyScroll();
    this.noMoreMessages = false;
    this.messages = [];
    this.currentPage = 1;
    this.hasNextPage = false;
    this.hasPrevPage = false;
  }

  public viewAllMessages(): void {
    this.closeMessages();
    this._router.navigate([this.routeAll]);
  }

  public goToMessage(message: any): void {
    let chatId = message.chatId;

    if (!chatId) {
      const fromId = message.fromId?._id || '';
      const toId = message.toId?._id || '';
      const fromType = message.fromType || '';
      const toType = message.toType || '';
      chatId = `${fromId}-${fromType}|${toId}-${toType}`;
    }

    this.closeMessages();

    this._router.navigate(['/players/players-chat'], {
      queryParams: { chatId },
    });
  }

  public markAsRead(message: any): void {
    this._messageService.updateStatusRead(message._id, true).subscribe({
      next: () => {
        this.messages = this.messages.filter((m) => m !== message);
        this.totalCount = Math.max(0, this.totalCount - 1);
        this.messageCountChange.emit(this.totalCount);
      },
      error: (error) => {},
    });
  }

  public onMouseEnter(): void {
    if (this.mobileMode) return;

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }
    this.showMessages = true;
    this.disableBodyScroll();
    if (this.messages.length === 0 && !this.noMoreMessages) {
      this.getAllMessages();
    }
  }

  public onMouseLeave(): void {
    if (this.mobileMode) return;

    this.hoverTimeout = setTimeout(() => {
      this.showMessages = false;
      this.enableBodyScroll();
    }, 300);
  }

  public onScroll(event: any): void {
    const element = event.target;
    const atBottom =
      element.scrollHeight - element.scrollTop <= element.clientHeight + 50;

    if (atBottom && !this.isLoading && this.hasNextPage) {
      this.getAllMessages(this.currentPage + 1);
    }
  }

  private disableBodyScroll(): void {
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;
    }
  }

  private enableBodyScroll(): void {
    if (window.innerWidth <= 768) {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  }

  public toRedirectPage(route: string): void {
    window.location.href = route;
  }
}
